// src/utils/api.js
import { clearStoredAuth, getStoredToken } from "./auth";

// Automatically detect local or production environment
const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

// Use the right backend based on where the site runs
const BASE_URL =
  (isLocalhost
    ? process.env.REACT_APP_API_URL_LOCAL
    : process.env.REACT_APP_API_URL_PROD) ||
  "http://localhost:5000";

// We keep "/api" only here - not in each fetch path
export const API_BASE = `${BASE_URL}/api`;

// ----------------------------
// Generic request wrapper
// ----------------------------

export async function apiFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const isFormData = options.body instanceof FormData;
  const storedToken = getStoredToken();

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (storedToken && !headers.Authorization) {
    headers.Authorization = `Bearer ${storedToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 401) {
      clearStoredAuth();
    }
    console.error("API error response:", res.status, data);
    const error = new Error(data.message || "API error");
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

// ----------------------------
// Auth endpoints
// ----------------------------
export function register(user) {
  return apiFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify(user),
  });
}

export function verifyRegistrationOtp(payload) {
  return apiFetch("/auth/register/verify", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function login(credentials) {
  return apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
}

export function getMe() {
  return apiFetch("/auth/me");
}

export function searchSite(query = "", limit = 12) {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
  }
  params.set("limit", String(limit));

  return apiFetch(`/search?${params.toString()}`);
}

// ----------------------------
// SC/ST & Matrimonial Admin
// ----------------------------
export function getSCSTSubmissions() {
  return apiFetch("/admin/scst-submissions");
}

export function getMatrimonialSubmissions() {
  return apiFetch("/admin/matrimonial");
}

const ADMIN_COLLECTION_PATHS = {
  users: "users",
  "scst-submissions": "scst-submissions",
  scst: "scst-submissions",
  matrimonial: "matrimonial",
  blogs: "blogs",
  categories: "categories",
  recipients: "recipients",
  "content-requests": "content-requests",
  "privacy-requests": "privacy-requests",
  menus: "menus",
  personalities: "personalities",
  temples: "temples",
  businesses: "businesses",
};

function resolveAdminCollectionPath(type) {
  return ADMIN_COLLECTION_PATHS[type] || type;
}

export function deleteSubmission(type, id) {
  const path = resolveAdminCollectionPath(type);
  return apiFetch(`/admin/${path}/${id}`, { method: "DELETE" });
}

export function bulkDeleteAdminItems(type, ids) {
  const path = resolveAdminCollectionPath(type);
  return apiFetch(`/admin/${path}/bulk-delete`, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export function uploadAdminImage(file) {
  const formData = new FormData();
  formData.append("image", file);

  return apiFetch("/admin/blogs/upload", {
    method: "POST",
    body: formData,
  });
}

// ----------------------------
// Recipients & Users
// ----------------------------
export function getRecipients() {
  return apiFetch("/admin/recipients");
}

export function addRecipient(email) {
  return apiFetch("/admin/recipients", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function deleteRecipient(id) {
  return apiFetch(`/admin/recipients/${id}`, { method: "DELETE" });
}

// ----------------------------
// User Management
// ----------------------------
export function updateUserRole(userId, role) {
  return apiFetch(`/admin/users/${userId}/role`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
}

export function createUser(data) {
  return apiFetch("/admin/create-user", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getStudentOverview() {
  return apiFetch("/students/overview");
}

export function getBusinessDirectory(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      searchParams.set(key, String(value).trim());
    }
  });

  const query = searchParams.toString();
  return apiFetch(`/businesses${query ? `?${query}` : ""}`);
}

export function getBusinessBySlug(slug) {
  return apiFetch(`/businesses/${slug}`);
}

export function submitBusinessListing(payload) {
  return apiFetch("/businesses/submit", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function uploadBusinessImage(file) {
  const formData = new FormData();
  formData.append("image", file);

  return apiFetch("/businesses/upload", {
    method: "POST",
    body: formData,
  });
}

export function getStudentCatalog() {
  return apiFetch("/students/catalog");
}

export function getExamDashboard(examSlug) {
  return apiFetch(`/students/exams/${examSlug}/dashboard`);
}

export function getExamTests(examSlug, variantSlug = "") {
  const params = new URLSearchParams();
  if (variantSlug) {
    params.set("variant", variantSlug);
  }
  return apiFetch(
    `/students/exams/${examSlug}/tests${
      params.toString() ? `?${params.toString()}` : ""
    }`
  );
}

export function getTestOverview(testId) {
  return apiFetch(`/students/tests/${testId}`);
}

export function createExamAttempt(testId) {
  return apiFetch(`/students/tests/${testId}/attempts`, {
    method: "POST",
  });
}

export function getAttemptDetail(attemptId) {
  return apiFetch(`/students/attempts/${attemptId}`);
}

export function saveAttemptAnswer(attemptId, payload) {
  return apiFetch(`/students/attempts/${attemptId}/answers`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function saveAttemptSectionState(attemptId, payload) {
  return apiFetch(`/students/attempts/${attemptId}/section-state`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function submitAttempt(attemptId) {
  return apiFetch(`/students/attempts/${attemptId}/submit`, {
    method: "POST",
  });
}

export function getExamAttempts(examSlug) {
  return apiFetch(`/students/exams/${examSlug}/attempts`);
}

export function getAttemptResult(attemptId) {
  return apiFetch(`/students/attempts/${attemptId}/result`);
}
